import { Overlay } from '@angular/cdk/overlay';
import { AfterViewInit, Component, inject, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { GuardsCheckEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { ErrorAlertComponent, GlobalComponentService, IndeterminateProgressBarComponent, InfoAlertComponent, InfoConfirmComponent, removeUrlQueryParam, TmModalCloseReason, TmModalData } from '@techmore/global';
import { Response } from 'express';
import { merge, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { ServerConfigService } from './share/service/server-config.service';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from './share/tokens/generator-env.token';
import { IS_BROWSER_TOKEN } from './share/tokens/is-browser.token';
import { IS_SERVER_TOKEN } from './share/tokens/is-server.token';
import { removeAnchorHref } from './util';
import { RESPONSE } from '../express.tokens';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(InfoAlertComponent) infoAlert: InfoAlertComponent;
  @ViewChild(InfoConfirmComponent) infoConfirm: InfoConfirmComponent;
  @ViewChild(ErrorAlertComponent) errorAlert: ErrorAlertComponent;
  @ViewChild(IndeterminateProgressBarComponent) indeterminateProgressBar: IndeterminateProgressBarComponent;
  private readonly serverConfigService = inject(ServerConfigService);
  defaultInfoTitle = '';
  defaultInfoAlertTitle = '';
  defaultErrorAlertTitle = '';
  isBrowser = inject(IS_BROWSER_TOKEN);
  isServer = inject(IS_SERVER_TOKEN);
  alertModalConfig: MatDialogConfig<TmModalData> = {role: 'alertdialog', hasBackdrop: true }
  ngUnsub$ = new Subject<void>();

  constructor(
    private readonly globalComponentService: GlobalComponentService,
    private readonly router: Router,
    @Inject(GENERATOR_ENV_TOKEN) private readonly generatorEnv: GeneratorEnv,
    @Optional() @Inject(RESPONSE) private readonly response: Response,
    private readonly overlay: Overlay,
  ) {
    this.alertModalConfig = {role: 'alertdialog', hasBackdrop: true, scrollStrategy: this.overlay.scrollStrategies.noop() }
  }

  ngOnInit(): void {
    this.globalComponentService.globalCmp = this;

    this.router.events.pipe(
      filter(evt => evt instanceof NavigationError),
      takeUntil(this.ngUnsub$)
    ).subscribe((evt: NavigationError) => {
      console.error('router errorHandler', evt);
      if (this.isServer) {
        this.response?.redirect(301, `${this.serverConfigService.baseHref}404.html`);
      } else {
        // or redirect to default route
        window.location.href = `${window.location.origin}${this.serverConfigService.baseHref}404.html`;
      }
    })

    let isUrlPathChange = false;
    this.router.events.pipe(
      filter(evt => evt instanceof GuardsCheckEnd || evt instanceof NavigationStart),
      takeUntil(this.ngUnsub$)
    ).subscribe((evt) => {
      if (evt instanceof NavigationStart) {
        isUrlPathChange = removeAnchorHref(removeUrlQueryParam(this.router.url.replace('.html',''))) !== removeAnchorHref(removeUrlQueryParam(evt.url.replace('.html','')));

        if (this.isBrowser && isUrlPathChange) {
          window.location.href = `${window.location.origin}${evt['url']}`;
        }
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.defaultInfoTitle = this.infoConfirm.title;
      this.defaultInfoAlertTitle = this.infoAlert.title;
      this.defaultErrorAlertTitle = this.errorAlert.title;
    }
  }

  ngOnDestroy(): void {
    this.ngUnsub$.next();
    this.ngUnsub$.complete();
  }

  openInfoAlert(message: string, param?: { title?: string, afterClose?: () => void }): InfoAlertComponent|undefined {
    if (!this.globalComponentService.globalCmp) {
      console.error('globalComponentService.globalCmp is null');
      console.info(message);
      return;
    }
    if (!this.infoAlert) { return undefined; }
    if (!this.isBrowser) { return this.infoAlert; }
    this.infoAlert.message = message;
    if (param) {
      this.infoAlert.title = param.title || this.defaultInfoAlertTitle;
      if (param.afterClose) {
        merge(
          this.infoAlert.afterConfirm,
          this.infoAlert.afterCancel,
        ).pipe(take(1))
          .subscribe(() => {
            (param.afterClose as () => void)();
          });
      }
    }

    if (!this.infoAlert.modal.isOpened) {
      this.infoAlert.open();
    }
    return this.infoAlert;
  }

  openInfoConfirm(message: string, param?: { title?: string, afterConfirm?: () => void, afterClose?: () => void }): InfoConfirmComponent|undefined {
    if (!this.globalComponentService.globalCmp) {
      console.error('globalComponentService.globalCmp is null');
      console.info(message);
      return;
    }
    if (!this.infoConfirm) { return undefined; }
    if (!this.isBrowser) { return this.infoConfirm; }
    this.infoConfirm.message = message;
    if (param) {
      this.infoConfirm.title = param.title || this.defaultInfoTitle;
      if (param.afterConfirm || param.afterClose) {
        let closeReason: TmModalCloseReason;
        merge(
          this.infoConfirm.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
          this.infoConfirm.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
        ).pipe(take(1))
          .subscribe(() => {
            if (closeReason === TmModalCloseReason.SUBMIT) {
              (param.afterConfirm as () => void)();
            } else {
              (param.afterClose as () => void)();
            }
          });
      }
    }

    if (!this.infoConfirm.modal.isOpened) {
      this.infoConfirm.open();
    }
    return this.infoConfirm;
  }

  openErrorAlert(message: string, param?: { title?: string, afterConfirm?: () => void, afterClose?: () => void, showCopyTip?: boolean }): ErrorAlertComponent|undefined {
    if (!this.globalComponentService.globalCmp) {
      console.error('globalComponentService.globalCmp is null');
      console.error(message);
      return;
    }
    if (!this.errorAlert) { return undefined; }
    if (!this.isBrowser) { return this.errorAlert as ErrorAlertComponent; }
    this.errorAlert.message = message;
    if (param) {
      this.errorAlert.title = param.title || this.defaultErrorAlertTitle;
      merge(
        this.errorAlert.afterConfirm,
        this.errorAlert.afterCancel,
      ).pipe(take(1))
        .subscribe(() => {
          if (param.afterClose) {
            (param.afterClose as () => void)();
          }
          if (param.showCopyTip) {
            this.errorAlert.showCopyButton = false;
          }
        });
      if (param.showCopyTip) {
        this.errorAlert.showCopyButton = true;
      }
    }

    if (!this.errorAlert.modal.isOpened) {
      this.errorAlert.open();
    }
    return this.errorAlert as ErrorAlertComponent;
  }

  openIndeterminateProgressBar() {
    if (!this.isBrowser) { return; }
    this.indeterminateProgressBar?.show();
  }

  closeIndeterminateProgressBar() {
    if (!this.isBrowser) { return; }
    this.indeterminateProgressBar?.hide();
  }
}
